import React, { useState, useRef, useEffect, useMemo } from 'react';
import moment from 'moment';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { CalendarOutlined, DownOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import styles from './styles';
import './style.css';
import {
  DateRange,
  QuickSelectOption,
  RBHDateRangePickerProps,
  MSG,
  QuickSelectKey,
} from './types';
import QuickSelectSidebar from './QuickSelectSidebar';
import CalendarGrid from './CalendarGrid';
import { Colors } from '../../theme';

export type { DateRange };

const buildAllQuickSelectOptions = (
  today: moment.Moment,
  effectiveToday: moment.Moment
): Record<QuickSelectKey, QuickSelectOption> => {
  const sub = (n: number, unit: moment.unitOfTime.DurationConstructor): DateRange => ({
    from: effectiveToday.clone().subtract(n, unit),
    to: effectiveToday.clone(),
  });
  return {
    today: { labelId: MSG.today, getValue: () => ({ from: today.clone(), to: today.clone() }) },
    yesterday: {
      labelId: MSG.yesterday,
      getValue: () => { const d = today.clone().subtract(1, 'day'); return { from: d, to: d.clone() }; },
    },
    last7days:  { labelId: MSG.last7days,  getValue: () => sub(6, 'day') },
    last14days: { labelId: MSG.last14days, getValue: () => sub(13, 'day') },
    last30days: { labelId: MSG.last30days, getValue: () => sub(29, 'day') },
    thisWeek:   { labelId: MSG.thisWeek,   getValue: () => ({ from: effectiveToday.clone().startOf('week'), to: effectiveToday.clone() }) },
    monthToYesterday:  { labelId: MSG.monthToYesterday,  getValue: () => ({ from: today.clone().startOf('month'), to: today.clone().subtract(1, 'day') }) },
    lastMonth: {
      labelId: MSG.lastMonth,
      getValue: () => ({
        from: today.clone().subtract(1, 'month').startOf('month'),
        to: today.clone().subtract(1, 'month').endOf('month'),
      }),
    },
  };
};

const RBHDateRangePicker: React.FC<RBHDateRangePickerProps> = ({
  label,
  value,
  onChange,
  disabled,
  maxDateRange,
  minDate,
  maxDate,
  disabledDates,
  quickSelect,
  style,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState<DateRange>(
    value || { from: undefined, to: undefined }
  );
  const [currentMonth, setCurrentMonth] = useState<moment.Moment>(
    moment().startOf('month')
  );
  const [hoveredDate, setHoveredDate] = useState<moment.Moment | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) setSelectedRange(value || { from: undefined, to: undefined });
  }, [value, open]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const { today, effectiveToday } = useMemo(() => {
    const t = moment();
    return {
      today: t,
      effectiveToday: maxDate && maxDate.isBefore(t, 'day') ? maxDate.clone() : t.clone(),
    };
  }, [maxDate]);


  const quickSelectOptions = useMemo((): QuickSelectOption[] => {
    const allOptions = buildAllQuickSelectOptions(today, effectiveToday);
    return quickSelect
      ? quickSelect.map((item) => typeof item === 'string' ? allOptions[item] : item)
      : Object.values(allOptions) as QuickSelectOption[];
  }, [quickSelect, today, effectiveToday]);

  const formatRangeSubtitle = (range: DateRange): React.ReactNode => {
    if (!range.from || !range.to) return null;
    const fmt = { day: 'numeric' as const, month: 'short' as const };
    if (range.from.isSame(range.to, 'day')) {
      return <FormattedDate value={range.from.toDate()} {...fmt} />;
    }
    return (
      <>
        <FormattedDate value={range.from.toDate()} {...fmt} />
        {' - '}
        <FormattedDate value={range.to.toDate()} {...fmt} />
      </>
    );
  }

  const quickSelectSubtitles = useMemo(
    () => quickSelectOptions.map((o) => formatRangeSubtitle(o.getValue())),
    [quickSelectOptions]
  );

  const isRangeDisabled = (range: DateRange): boolean => {
    if (!range.from || !range.to) return false;
    const from = range.from.clone().startOf('day');
    const to = range.to.clone().startOf('day');
    if (minDate && from.isBefore(minDate.clone().startOf('day'))) return true;
    if (maxDate && to.isAfter(maxDate.clone().startOf('day'))) return true;
    return disabledDates?.some((d) => !d.isBefore(from, 'day') && !d.isAfter(to, 'day')) ?? false;
  };

  const quickSelectDisabled = useMemo(
    () => quickSelectOptions.map((o) => isRangeDisabled(o.getValue())),
    [quickSelectOptions, minDate, maxDate, disabledDates]
  );

  const isDateDisabled = (date: moment.Moment) => {
    const normalized = date.clone().startOf('day');
    if (minDate && normalized.isBefore(minDate.clone().startOf('day'))) return true;
    if (maxDate && normalized.isAfter(maxDate.clone().startOf('day'))) return true;
    if (maxDateRange && selectedRange?.from && !selectedRange.to) {
      if (Math.abs(date.diff(selectedRange.from, 'day')) >= maxDateRange) return true;
    }
    return disabledDates?.some((d) => d.isSame(date, 'day')) ?? false;
  };

  const handleDateClick = (date: moment.Moment) => {
    if (isDateDisabled(date)) return;
    if (!selectedRange?.from || selectedRange.to) {
      setSelectedRange({ from: date, to: undefined });
    } else if (date.isBefore(selectedRange.from, 'day')) {
      setSelectedRange({ from: date, to: selectedRange.from });
    } else {
      setSelectedRange({ from: selectedRange.from, to: date });
    }
  };

  const hasRange = !!(selectedRange?.from && selectedRange?.to);
  const formattedRange = hasRange
    ? `${selectedRange.from!.format('DD/MM/YYYY')} - ${selectedRange.to!.format('DD/MM/YYYY')}`
    : null;

  const isOccupied = open || hasRange;
  const labelStyle: React.CSSProperties = isOccupied
    ? { ...styles.floatingLabel, color: open ? Colors.rbhViolet600 : styles.floatingLabel.color }
    : { ...styles.floatingLabel, ...styles.triggerText, top: 10, left: 32, fontSize: 14, background: 'transparent', color: Colors.textPrimary };

  const calendarGridProps = { selectedRange, hoveredDate, today, onDateClick: handleDateClick, onDateHover: setHoveredDate, isDateDisabled };

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'block', ...style }}>
      {label && <span style={labelStyle}>{label}</span>}

      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        style={{
          ...styles.triggerBase,
          border: `1px solid ${open ? Colors.rbhViolet600 : '#d9d9d9'}`,
          color: '#6d6d6d',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.4 : 1,
        }}
      >
        <CalendarOutlined translate={null} style={{ ...styles.triggerIcon, color: '#6d6d6d' }} />
        <span style={{ ...styles.triggerText, visibility: isOccupied ? 'visible' : 'hidden' }}>
          {formattedRange}
        </span>
        <DownOutlined
          translate={null}
          style={{ ...styles.triggerChevron, color: '#6d6d6d', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>

      {open && (
        <div style={styles.dropdown}>
          <QuickSelectSidebar
            options={quickSelectOptions}
            subtitles={quickSelectSubtitles}
            disabledOptions={quickSelectDisabled}
            onSelect={(option) => {
              const range = option.getValue();
              setSelectedRange(range);
              if (range.from) {
                const fromMonth = range.from.clone().startOf('month');
                const pane1 = currentMonth;
                const pane2 = currentMonth.clone().add(1, 'month');
                const visibleInPane1 = fromMonth.isSame(pane1, 'month');
                const visibleInPane2 = fromMonth.isSame(pane2, 'month');
                if (!visibleInPane1 && !visibleInPane2) {
                  setCurrentMonth(fromMonth);
                }
              }
            }}
          />
          <div style={styles.calendarSection}>
            <div style={styles.calendarNav}>
              <button
                type="button"
                className="rbh-drp-v2-nav-btn"
                onClick={() => setCurrentMonth(currentMonth.clone().subtract(1, 'month'))}
                style={styles.navBtn}
              >
                <LeftOutlined translate={null} style={styles.navIcon} />
              </button>
              <div style={styles.calendarsWrapper}>
                <CalendarGrid month={currentMonth} {...calendarGridProps} />
                <CalendarGrid month={currentMonth.clone().add(1, 'month')} {...calendarGridProps} />
              </div>
              <button
                type="button"
                className="rbh-drp-v2-nav-btn"
                onClick={() => setCurrentMonth(currentMonth.clone().add(1, 'month'))}
                style={styles.navBtn}
              >
                <RightOutlined translate={null} style={styles.navIcon} />
              </button>
            </div>
            <div style={styles.actionsRow}>
              <button
                type="button"
                className="rbh-drp-v2-cancel-btn"
                onClick={() => { setSelectedRange(value ?? { from: undefined, to: undefined }); setOpen(false); }}
                style={styles.cancelBtn}
              >
                <FormattedMessage id={MSG.cancel} />
              </button>
              <button
                type="button"
                className="rbh-drp-v2-apply-btn"
                onClick={() => { onChange(selectedRange); setOpen(false); }}
                style={styles.applyBtn}
              >
                <FormattedMessage id={MSG.apply} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default RBHDateRangePicker;
